import { z } from "zod/v4";

import { ApiDto, ApiDtoSchema } from "./apis";

export type DefaultIntegrationGlobalSettingsDto = object;

/**
 * How the org first connected Slack. This is set once and never changed.
 *
 * This is historical data, not the connection's current capabilities. Check
 * `accessToken` to tell whether we can use the customer's workspace or only
 * the shared channel.
 */
export const SlackConnectionMethodList = [
  "admin_oauth",
  "member_connect",
] as const;

export type SlackConnectionMethod = (typeof SlackConnectionMethodList)[number];

export const IntegrationRecordStatusList = [
  "active",
  "inactive",
  "degraded",
  "suspended",
] as const;

export type IntegrationRecordStatus =
  (typeof IntegrationRecordStatusList)[number];

export const IntegrationNameList = [
  "Recharge",
  "StayAi",
  "GrowLTV",
  "OneClickUpsell",
  "Google",
  "Amped",
  "Clarity",
  "Hotjar",
  "Heatmap",
  "CustomApi",
  "Klaviyo",
  "Slack",
  "Amplitude",
  "Heap",
  "Segment",
  "IntelligemsAPI",
  "IntelligemsMcp",
  "UPS",
  "MetaAds",
] as const;

export type IntegrationName = (typeof IntegrationNameList)[number];

export type HeadlessSupportedIntegration = "Heatmap" | "Hotjar" | "Clarity";

export const IntegrationTypeList = [
  "SUBSCRIPTION",
  "UI",
  "ANALYTICS",
  "API",
  "SHIPPING",
] as const;

export type IntegrationTypeType = (typeof IntegrationTypeList)[number];

export const BaseIntegrationDtoSchema = z.object({
  id: z.uuid(),
  name: z.enum(IntegrationNameList),
  description: z.string(),
  enabled: z.boolean(),
  globalSettings: z.any(),
  apis: ApiDtoSchema.array().default([]),
  createdAtTs: z.number().optional(),
  lastUpdateTs: z.number().optional(),
});

export const CreateBaseIntegrationDtoSchema = BaseIntegrationDtoSchema.extend({
  id: z.uuid().optional(),
});

export interface BaseIntegrationDto<
  GlobalSettings = object,
  Internal = object,
> {
  id: string;
  name: IntegrationName;
  description: string;
  enabled: boolean;
  globalSettings?: GlobalSettings;
  internal?: Internal;
  apis: ApiDto[];
  createdAtTs?: number;
  lastUpdateTs?: number;
}

export type IntegrationDto = Awaited<
  ReturnType<typeof BaseIntegrationDtoSchema.parseAsync>
>;

export const IntegrationWithSensitiveFieldsDtoSchema =
  BaseIntegrationDtoSchema.extend({
    /** Set once at first connect. See `SlackConnectionMethodList`. */
    connectionMethod: z.enum(SlackConnectionMethodList).nullish(),
    accessToken: z.string().nullish(),
    refreshToken: z.string().nullish(),
    clientSecret: z.string().nullish(),
    scope: z.string().nullish(),
    expiresIn: z.number().nullish(),
    expiresAt: z.number().nullish(),
    internal: z.any().nullish(),
    organizationId: z.string().nullish(),
    agencyId: z.uuid().nullish(),
    status: z
      .enum(IntegrationRecordStatusList)
      .nullish()
      .transform((v) => v ?? undefined),
  });

export type IntegrationWithSensitiveFieldsDto = z.infer<
  typeof IntegrationWithSensitiveFieldsDtoSchema
>;

export const AvailableIntegrationSchema =
  IntegrationWithSensitiveFieldsDtoSchema.extend({
    cls: z.any(),
    type: z.enum(IntegrationTypeList),
    defaultExperienceSettings: z.any().nullish(),
  });

export type AvailableIntegrationType = z.infer<
  typeof AvailableIntegrationSchema
>;

export interface IgBaseIntegrationLikeObject {
  initializeAfterDom: () => void;
  earlyUpdateDom: () => any;
  lateUpdateDom: () => any;
  name: string;
  enabled: boolean;
}

export interface IgGaIntegrationLikeObject extends IgBaseIntegrationLikeObject {
  getTrackedExperiences: () => Record<string, true>;
  setTrackedExperiences: (gaVariationNames: Record<string, true>) => void;
}

export const CreateIntegrationDtoSchema =
  IntegrationWithSensitiveFieldsDtoSchema.omit({
    apis: true,
    connectionMethod: true,
  }).extend({
    id: z.uuid().optional(),
    organizationId: z.uuid().nullish(),
    status: z.enum(IntegrationRecordStatusList).optional(),
  });

export type CreateIntegrationDto = z.infer<typeof CreateIntegrationDtoSchema>;
